// Pure, SSR-safe SEO helpers. Strings come from the shared i18next store via
// getFixedT(locale), which works for any request clone because clones share
// the store.
import i18n from '@/i18n'
import { COMPANY, LINKS, SEO_ORIGIN, SOCIAL_PROFILES } from '@/lib/company'
import { SEO_PRIORITY_LOCALES } from '@/lib/seo'
import { DEFAULT_LOCALE, isValidLocale, withLocalePath } from '@/lib/i18n-utils'

export const HREFLANG_LOCALES = ['en-US', 'bs-BA'] as const

export const PRIVATE_ROUTE_PREFIXES = [
  '/admin',
  '/community/private',
  '/agents/callback',
  '/profile',
  '/dashboard',
  '/login',
  '/register',
  '/auth',
  '/workspace',
  '/first-time-setup',
  '/unauthorized',
  '/confirm-email',
  '/forgot-password',
  '/reset-password',
  '/my-page',
] as const

export type SeoPageKey =
  | 'whatWeDo'
  | 'industries'
  | 'cloudEngineering'
  | 'ai'
  | 'dataAnalytics'
  | 'workWithUs'
  | 'arkived'
  | 'forum'
  | 'community'
  | 'trust'
  | 'home'
  | 'cloud'
  | 'research'
  | 'iot'
  | 'products'
  | 'about'
  | 'contact'
  | 'blog'
  | 'privacy'
  | 'terms'

export function isPrivateRoute(normalizedPath: string): boolean {
  const p = normalizedPath.toLowerCase()
  return PRIVATE_ROUTE_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  )
}

const toAbsolute = (path: string) => `${SEO_ORIGIN}${path}`
const toOgLocale = (locale: string) => locale.replace('-', '_')

export function buildLocalizedSeoHead(normalizedPath: string, locale: string) {
  const pageLocale = isValidLocale(locale) ? locale : DEFAULT_LOCALE
  const isPrivate = isPrivateRoute(normalizedPath)
  const isIndexable = (SEO_PRIORITY_LOCALES as ReadonlyArray<string>).includes(
    pageLocale,
  )
  const canonicalLocale = isIndexable ? pageLocale : DEFAULT_LOCALE
  const canonicalUrl = toAbsolute(
    withLocalePath(normalizedPath, canonicalLocale),
  )
  // R12: hreflang alternates only ever declare HREFLANG_LOCALES (en-US, bs-BA)
  // as targets, so a page whose OWN locale isn't in that set (e.g. hr-HR,
  // indexable but not a hreflang locale) must not emit alternates at all —
  // otherwise it announces en-US/bs-BA without being announced back by them,
  // a non-reciprocal cluster search engines penalize.
  const isHreflangLocale = (HREFLANG_LOCALES as ReadonlyArray<string>).includes(
    pageLocale,
  )
  const alternates =
    isPrivate || !isIndexable || !isHreflangLocale
      ? []
      : [
          ...HREFLANG_LOCALES.map((l) => ({
            hreflang: l,
            href: toAbsolute(withLocalePath(normalizedPath, l)),
          })),
          {
            hreflang: 'x-default',
            href: toAbsolute(withLocalePath(normalizedPath, DEFAULT_LOCALE)),
          },
        ]
  return {
    canonicalUrl,
    alternates,
    robots: isPrivate
      ? 'noindex,nofollow'
      : isIndexable
        ? 'index,follow'
        : 'noindex,follow',
    ogLocale: toOgLocale(canonicalLocale),
  }
}

function seoT(locale: string | undefined) {
  const l = locale && isValidLocale(locale) ? locale : DEFAULT_LOCALE
  return i18n.getFixedT(l, 'seo')
}

export function seoStrings(
  pageKey: SeoPageKey | 'default',
  locale: string | undefined,
) {
  const t = seoT(locale)
  const prefix = pageKey === 'default' ? 'default' : `pages.${pageKey}`
  return {
    title: t(`${prefix}.title`, { defaultValue: t('default.title') }),
    description: t(`${prefix}.description`, {
      defaultValue: t('default.description'),
    }),
  }
}

export function localizedPageHead(
  pageKey: SeoPageKey,
  locale: string | undefined,
) {
  const { title, description } = seoStrings(pageKey, locale)
  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
  }
}

export const jsonLd = (obj: unknown) => ({
  type: 'application/ld+json' as const,
  children: JSON.stringify(obj).replace(/</g, '\\u003c'),
})

/** getFixedT(locale, 'common')(key) with locale normalization, for nav labels. */
export function navLabel(locale: string, key: string): string {
  const l = isValidLocale(locale) ? locale : DEFAULT_LOCALE
  return i18n.getFixedT(l, 'common')(key)
}

export function breadcrumbLd(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

// Top-level public paths that get a Home -> Page breadcrumb from the
// {-$locale} layout head. Deeper paths (e.g. blog posts) build their own.
const TOP_LEVEL_PAGE_KEYS: Record<string, SeoPageKey> = {
  '/what-we-do': 'whatWeDo',
  '/industries': 'industries',
  '/cloud-engineering': 'cloudEngineering',
  '/ai': 'ai',
  '/data-analytics': 'dataAnalytics',
  '/work-with-us': 'workWithUs',
  '/about': 'about',
  '/contact': 'contact',
  '/blog': 'blog',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/cloud': 'cloud',
  '/research': 'research',
  '/trust': 'trust',
  '/iot': 'iot',
  '/products': 'products',
}

// nav.<key> to use as the breadcrumb label for a page, when one exists.
// /about is handled separately (nav.about is an object with a nested title).
// Pages without an entry (privacy, terms) fall back to the seo page title.
const NAV_LABEL_KEY: Partial<Record<SeoPageKey, string>> = {
  whatWeDo: 'nav.whatWeDo',
  industries: 'nav.industries',
  workWithUs: 'nav.contact',
  cloud: 'nav.cloud',
  research: 'nav.research',
  iot: 'nav.iot',
  products: 'nav.products',
  blog: 'nav.writing',
  contact: 'nav.contact',
}

function stripBrandSuffix(title: string): string {
  return title.replace(/ (?:—|\|) Horizon Tech$/, '')
}

function breadcrumbPageLabel(
  normalizedPath: string,
  pageKey: SeoPageKey,
  locale: string,
): string {
  if (normalizedPath === '/about') {
    const aboutRaw = i18n.getResource(locale, 'common', 'nav.about')
    const key =
      aboutRaw && typeof aboutRaw === 'object' ? 'nav.about.title' : 'nav.about'
    const label = navLabel(locale, key)
    if (label !== key) return label
  } else {
    const navKey = NAV_LABEL_KEY[pageKey]
    if (navKey) {
      const label = navLabel(locale, navKey)
      if (label !== navKey) return label
    }
  }
  return stripBrandSuffix(seoStrings(pageKey, locale).title)
}

/**
 * BreadcrumbList JSON-LD (Home -> Page) for a top-level public page, or null
 * for home itself, private routes, and paths deeper than one segment (e.g.
 * blog posts build their own Home -> Writing -> post-title breadcrumb).
 */
export function buildTopLevelBreadcrumbLd(
  normalizedPath: string,
  locale: string,
) {
  if (normalizedPath === '/' || isPrivateRoute(normalizedPath)) return null
  const segments = normalizedPath.split('/').filter(Boolean)
  if (segments.length !== 1) return null
  const pageKey = TOP_LEVEL_PAGE_KEYS[normalizedPath]
  if (!pageKey) return null
  return breadcrumbLd([
    {
      name: navLabel(locale, 'mobileNav.home'),
      url: buildLocalizedSeoHead('/', locale).canonicalUrl,
    },
    {
      name: breadcrumbPageLabel(normalizedPath, pageKey, locale),
      url: buildLocalizedSeoHead(normalizedPath, locale).canonicalUrl,
    },
  ])
}

const ORG_ID = `${SEO_ORIGIN}/#organization`
const WEBSITE_ID = `${SEO_ORIGIN}/#website`

export function organizationLd(locale: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: COMPANY.brand,
    legalName: COMPANY.legalName,
    url: SEO_ORIGIN,
    logo: COMPANY.logo,
    email: COMPANY.email,
    telephone: COMPANY.phoneTel,
    description: seoStrings('default', locale).description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY.street,
      addressLocality: COMPANY.locality,
      postalCode: COMPANY.postalCode,
      addressCountry: COMPANY.countryCode,
    },
    sameAs: [...SOCIAL_PROFILES],
  }
}

export function websiteLd(locale: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SEO_ORIGIN,
    name: COMPANY.brand,
    inLanguage: locale,
    publisher: { '@id': ORG_ID },
  }
}

export function softwareApplicationLd(input: {
  name: string
  url: string
  description: string
  codeRepository?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: input.name,
    url: input.url,
    description: input.description,
    applicationCategory: 'DeveloperApplication',
    ...(input.codeRepository ? { sameAs: [input.codeRepository] } : {}),
    publisher: { '@id': ORG_ID },
  }
}

export function blogPostingLd(input: {
  url: string
  title: string
  description: string
  image: string
  datePublished?: string
  author?: string
  locale: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${input.url}#article`,
    headline: input.title,
    description: input.description,
    image: input.image,
    url: input.url,
    inLanguage: input.locale,
    ...(input.datePublished ? { datePublished: input.datePublished } : {}),
    author: input.author
      ? { '@type': 'Person', name: input.author }
      : { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBSITE_ID },
  }
}

export const ARKIVED_LINKS = { url: LINKS.arkived, repo: LINKS.arkivedRepo }

/** Product detail breadcrumbs include the product overview, unlike top-level pages. */
export function productPageHead(
  product: 'arkived',
  locale: string | undefined,
) {
  const resolved = locale && isValidLocale(locale) ? locale : DEFAULT_LOCALE
  return {
    ...localizedPageHead(product, resolved),
    scripts: [
      jsonLd(
        breadcrumbLd([
          {
            name: navLabel(resolved, 'mobileNav.home'),
            url: buildLocalizedSeoHead('/', resolved).canonicalUrl,
          },
          {
            name: navLabel(resolved, 'nav.products'),
            url: buildLocalizedSeoHead('/products', resolved).canonicalUrl,
          },
          {
            name: 'arkived',
            url: buildLocalizedSeoHead(`/products/${product}`, resolved)
              .canonicalUrl,
          },
        ]),
      ),
    ],
  }
}
